import React, { useRef, useState, useEffect } from 'react';
import NavBar from '../../NavBar';
import logo from '../../../images/bluelogo.png';
import { IoMdCheckmarkCircle } from 'react-icons/io';
import { FaRegFilePdf } from 'react-icons/fa';

// vamos usar a função de PDF
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { generatePDF } from '.././pdfGenerator';
import NewNotificacaoPDF from '../notificacao/newNotificacaoPDF';
import NewNotificacaoPreenchido from './newNotificacaoPreenchido';
import {
	requestInputsChoices,
	requestSetores,
	requestNewNoteCreate,
	requestUnitDetails,
} from '../../../services/newNotesRequest';

const nonEmptyString = z.string().refine((val) => val.trim().length > 0, {
	message: 'campo obrigatório',
});

const formSchema = z
	.object({
		unidade: nonEmptyString,
		dataNotificacao: nonEmptyString,
		data_ocorrencia: nonEmptyString,
		setor: nonEmptyString,
		turno: nonEmptyString,
		envolve_paciente: z.string(),
		sexo_paciente: z.string().optional(),
		nascimento_paciente: z.string().optional(),
		prontuario: z.string().optional(),
		nome_paciente: z.string().optional(),
		notificante: z.string().optional(),
		notificante_setor: nonEmptyString,
		notificante_cargo: z.string().optional(),
		tipo_incidente: nonEmptyString,
		tipo_incidente_outros: z.string().optional(),
		fato_presenciado: z.string(),
		quem_presenciou: z.string().optional(),
		descricao: nonEmptyString.min(3, 'campo obrigatório'),
		acoes_imediatas: nonEmptyString.min(3, 'campo obrigatório'),
		medida_preventiva: nonEmptyString.min(3, 'campo obrigatório'),
	})
	.superRefine((data, ctx) => {
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const parseDate = (val) => {
			if (!val) return null;
			const parts = String(val).split('-').map(Number);
			if (parts.length !== 3) return null;
			const [y, m, d] = parts;
			const dd = new Date(y, m - 1, d);
			return isNaN(dd.getTime()) ? null : dd;
		};

		const not = parseDate(data.dataNotificacao);
		const oc = parseDate(data.data_ocorrencia);
		const nasc = parseDate(data.nascimento_paciente);

		// 1. Datas futuras não permitidas
		if (not && not > today) {
			ctx.addIssue({
				path: ['dataNotificacao'],
				code: z.ZodIssueCode.custom,
				message: 'A data não pode ser futura',
			});
		}
		if (oc && oc > today) {
			ctx.addIssue({
				path: ['data_ocorrencia'],
				code: z.ZodIssueCode.custom,
				message: 'A data não pode ser futura',
			});
		}

		// 2. Ocorrência deve ser anterior ou igual à notificação
		if (not && oc && oc > not) {
			ctx.addIssue({
				path: ['data_ocorrencia'],
				code: z.ZodIssueCode.custom,
				message:
					'A data de ocorrência deve ser anterior ou igual à notificação',
			});
		}

		// 3. Nascimento não pode ser após ocorrência nem após hoje
		if (nasc) {
			if (nasc > today) {
				ctx.addIssue({
					path: ['nascimento_paciente'],
					code: z.ZodIssueCode.custom,
					message: 'A data não pode ser futura',
				});
			}
			if (oc && nasc > oc) {
				ctx.addIssue({
					path: ['nascimento_paciente'],
					code: z.ZodIssueCode.custom,
					message:
						'A data de nascimento não pode ser posterior à data da ocorrência',
				});
			}
		}

		// 4. Campos obrigatórios APENAS se envolve_paciente = "true"
		if (data.envolve_paciente === 'true') {
			// Valida sexo separadamente (campo de rádio)
			if (!data.sexo_paciente || data.sexo_paciente === '') {
				ctx.addIssue({
					path: ['sexo_paciente'],
					code: z.ZodIssueCode.custom,
					message: 'campo obrigatório',
				});
			}

			// Valida outros campos de paciente
			const camposPaciente = [
				'nascimento_paciente',
				'prontuario',
				'nome_paciente',
			];
			camposPaciente.forEach((campo) => {
				const val = data[campo];
				if (
					val === undefined ||
					val === null ||
					val.trim?.() === '' ||
					val === ''
				) {
					ctx.addIssue({
						path: [campo],
						code: z.ZodIssueCode.custom,
						message: 'campo obrigatório',
					});
				}
			});
		}

		// 5. campo obrigatório APENAS se presenciado = "true"
		if (data.fato_presenciado === 'true') {
			if (!data.quem_presenciou || data.quem_presenciou.trim() === '') {
				ctx.addIssue({
					path: ['quem_presenciou'],
					code: z.ZodIssueCode.custom,
					message: 'campo obrigatório',
				});
			}
		}

		// 6. campo obrigatorio APENAS se incidente seja "OUTROS"
		if (data.tipo_incidente === 'OUTROS') {
			if (
				!data.tipo_incidente_outros ||
				data.tipo_incidente_outros.trim() === ''
			) {
				ctx.addIssue({
					path: ['tipo_incidente_outros'],
					code: z.ZodIssueCode.custom,
					message: 'campo obrigatório',
				});
			}
		}
	});

export default function NewNotificacao({ id, notificacao, cards }) {
	// Normaliza mensagens de erro genéricas do Zod
	const resolveMsg = (msg, fallback = 'campo obrigatório') => {
		if (!msg) return fallback;
		if (
			msg.toLowerCase().includes('invalid') ||
			msg.toLowerCase().includes('expected') ||
			msg.toLowerCase().includes('received')
		)
			return fallback;
		return msg;
	};
	const [sended, setSended] = useState(false);
	const [unitId, setUnitId] = useState(null);
	const [pdfBaixado, setPdfBaixado] = useState(false);
	const [requestResult, setRequestResult] = useState({});
	const [copiado, setCopiado] = useState(false);
	const [createError, setCreateError] = useState('');
	const pdfRef = useRef();
	const [formData, setFormData] = useState({
		nome: '',
		email: '',
		descricao: '',
	});
	const [choices, setChoices] = useState({});
	const [setores, setSetores] = useState([]);

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		clearErrors,
		unregister,
		formState: { errors },
	} = useForm({
		resolver: zodResolver(formSchema),
		shouldUnregister: true,
		mode: 'onChange',
		defaultValues: {
			dataNotificacao: new Date().toISOString().split('T')[0],
		},
	});

	// control flags
	const [triedToSubmit, setTriedToSubmit] = useState(false);

	// observar radios para condicionar campos
	const envolve_paciente = watch('envolve_paciente');
	const fato_presenciado = watch('fato_presenciado');
	const tipo_incidente = watch('tipo_incidente');

	useEffect(() => {
		if (envolve_paciente === 'false' || envolve_paciente === undefined) {
			unregister([
				'sexo_paciente',
				'nascimento_paciente',
				'prontuario',
				'nome_paciente',
			]);
		}
	}, [envolve_paciente, unregister]);

	// Limpa campo de quem presenciou quando não foi presenciado
	useEffect(() => {
		if (fato_presenciado === 'false') {
			setValue('quem_presenciou', '');
			clearErrors('quem_presenciou');
		}
	}, [fato_presenciado, setValue, clearErrors]);

	// Limpa campo de outros quando não é OUTROS
	useEffect(() => {
		if (tipo_incidente !== 'OUTROS') {
			setValue('tipo_incidente_outros', '');
			clearErrors('tipo_incidente_outros');
		}
	}, [tipo_incidente, setValue, clearErrors]);

	// on valid submit
	const onValid = (data) => {
		// salvar objeto final que será usado no PDF invisível
		setFormData(data);
		setTriedToSubmit(false);
		const handleCreate = async () => {
			const result = await requestNewNoteCreate({
				token_notificacao:
					id !== undefined
						? id
						: unitId.replace(/-/g, '').split('').reverse().join(''),
				...data,
			});

			if (!result.error) {
				setSended(true);
				setRequestResult(result);
				console.log('✅ Dados enviados:', result);
			} else {
				console.log('X Dados não enviados:', result, data);
				setCreateError(
					Object.values(result.error)[0] ||
						'Houve um erro na criação da notificação, tente novamente mais tarde',
				);
			}
		};
		handleCreate();
	};

	// on invalid submit
	const onInvalid = (err) => {
		// apenas marcar que usuário tentou submeter — isso faz aparecer os erros
		setTriedToSubmit(true);
		// opcional: rolar até o primeiro erro (ajuda UX)
		try {
			const firstKey = Object.keys(err)[0];
			const el = document.querySelector(`[name="${firstKey}"]`);
			if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
		} catch (e) {
			/* no-op */
		}
	};

	const submitHandler = (e) => {
		handleSubmit(onValid, onInvalid)();
	};

	const handleChoices = async () => {
		const result = await requestInputsChoices();
		setChoices(result);
	};

	const handleUnit = async () => {
		const result = await requestUnitDetails(
			decryptName(
				decodeURIComponent(
					String(window.location.pathname).split('notificacoes/')[1],
				),
			),
		);

		const setores = await requestSetores(
			id !== undefined ? id : result[0].id,
		);
		setUnitId(result[0].id);
		setSetores(setores);
	};

	const decryptName = (encodedName) => {
		try {
			const decoded = atob(encodedName);
			return decoded;
		} catch (e) {
			return '';
		}
	};

	useEffect(() => {
		handleUnit();
		handleChoices();
	}, []);

	const submitClass =
		Object.values(errors).length === 0 && triedToSubmit
			? 'bg-[#004A80] text-white'
			: 'border-[#E3E3E3] text-[#757575] border-[1px]';

	return (
		<div className='flex flex-col min-h-screen'>
			<NavBar />
			{sended === true ? (
				<div className='font-inter flex justify-center items-start w-[80%] mx-auto h-screen'>
					<div className='flex flex-col bg-[#F5F5F5] mt-[100px] items-center rounded-md max-w-[800px] gap-5 flex-grow shadow-lg'>
						<div className='bg-[#004A80] rounded-t-md w-full h-[20px] '>
							{' '}
						</div>
						<div className='text-[#00C0F3] '>
							<IoMdCheckmarkCircle className='size-[50px] sm:size-[100px]' />
						</div>
						<div className='text-3xl font-semibold text-[#004A80]'>
							{' '}
							Notificação Enviada com Sucesso{' '}
						</div>
						<div className='text-lg font-medium w-[80%] text-[#8b8b8b]'>
							{' '}
							Sua Notificação foi enviada para a equipe de
							classificação, acompanhe o andamento dela através do
							protocolo abaixo.{' '}
						</div>
						<div className='flex flex-col sm:text-4xl font-bold mt-2 sm:mt-[32px] text-[#004A80]'>
							<button
								onClick={() => {
									setCopiado(true);
									navigator.clipboard.writeText(
										requestResult.protocolo,
									);
								}}
								className='flex gap-3'>
								<p>Protocolo: {requestResult.protocolo} </p>
							</button>
							{copiado ? (
								<div className='flex flex-col justify-end '>
									<div className='text-[#a0a0a0] font-semibold text-end text-[12px]'>
										{' '}
										Protocolo copiado!{' '}
									</div>
								</div>
							) : null}
						</div>
						<>
							<p className='text-xs sm:text-lg mx-auto text-center sm:text-start text-[#a0a0a0] mt-4 '>
								{' '}
								Clique no botão abaixo para baixar o PDF da sua
								notificação{' '}
							</p>
							<button
								className='sm:w-35 flex items-center justify-center gap-2 border border-[#B74644] text-[#B74644] p-1 sm:py-2 sm:px-4 rounded-md hover:bg-[#B74644] hover:text-white transition-all duration-200 text-[10px] sm:text-base focus:outline-none cursor-pointer sm:mb-[40px]'
								onClick={() => {
									generatePDF(pdfRef);
									setPdfBaixado(true);
								}}>
								{pdfBaixado ? (
									'Download Concluído'
								) : (
									<>
										<FaRegFilePdf className='scale-[130%] text-[10px]' />{' '}
										Baixar PDF
									</>
								)}
							</button>
						</>
						<a
							className='text-[#00C0F3] text-[10px] sm:text-base mb-14 hover:text-[#004A80] transition-all duration-200'
							href='/hub-de-notificacoes'>
							{' '}
							voltar à consulta de protocolos{' '}
						</a>
					</div>
					<div
						style={{
							position: 'absolute',
							left: '-9999px',
							top: 0,
						}}>
						<NewNotificacaoPDF
							ref={pdfRef}
							notificacao={requestResult}
						/>
					</div>
				</div>
			) : (
				<div className='flex-1 font-inter'>
					<div
						className={` ${
							notificacao?.status === 'NOTIFICADO' &&
							cards?.card === 'NOTIFICADO' &&
							cards?.caso !== 'NOTIFICADO'
								? 'mt-[20px]'
								: 'mt-[80px]'
						}  w-[90%] sm:w-[95%] max-w-[1136px] mx-auto flex items-center justify-center flex-col`}>
						<div className='gap-[32px] flex flex-col w-full'>
							<div className='bg-[#F0F4F8] flex rounded-[4px] w-full content-center min-h-[142px] p-[32px] gap-[30px] justify-between lg:flex-row flex-col'>
								<div className='content-center items-center mx-auto'>
									<img
										className='w-[200px] sm:w-[373.5px]'
										src={logo}
										alt='logo'
									/>
								</div>
								<p className='text-[28px] sm:text-[32px] font-bold text-[#13335A] content-center max-w-[714px] text-center'>
									{' '}
									NOTIFICAÇÃO DE INCIDENTES E NÃO
									CONFORMIDADES{' '}
								</p>
							</div>
							{notificacao?.status === 'NOTIFICADO' &&
							cards?.card === 'NOTIFICADO' &&
							cards?.caso !== 'NOTIFICADO' ? (
								<div>
									<NewNotificacaoPreenchido
										notificacao={notificacao}
									/>
									<div
										style={{
											position: 'absolute',
											left: '-9999px',
											top: 0,
										}}>
										<NewNotificacaoPDF
											ref={pdfRef}
											notificacao={notificacao}
										/>
									</div>
									<button
										className='enabled:bg-[#E3E3E3] max-h-[40px] text-[#a3a3a3] enabled:text-[#757575] flex bg-[#d3d3d3]  py-2 px-2 rounded-md enabled:hover:bg-[#757575] enabled:hover:text-[#f0f0f0] transition-all duration-200 focus:outline-none font-semibold mt-5'
										onClick={() => {
											generatePDF(pdfRef);
										}}>
										{pdfBaixado ? (
											'Download Concluído'
										) : (
											<>
												<FaRegFilePdf className=' w-[40px] h-auto text-[10px]' />{' '}
												PDF
											</>
										)}
									</button>
								</div>
							) : (
								<form
									onSubmit={submitHandler}
									className='w-full mx-auto bg-white flex flex-col gap-[30px]'>
									<div className='flex flex-col text-start'>
										<p className='text-[#004A80] text-[16px] font-medium '>
											{' '}
											Unidade{' '}
										</p>
										<input
											{...register('unidade')}
											name='unidade'
											defaultValue={decryptName(
												decodeURIComponent(
													String(
														window.location
															.pathname,
													).split('notificacoes/')[1],
												),
											)}
											readOnly
											type='text'
											placeholder='Nome do Hospital'
											className='border-[1px] rounded-md text-gray-500 border-[#E3E3E3] py-1 px-2 placeholder:[#757575] h-[40px]'
										/>
										{triedToSubmit && errors.unidade && (
											<span className='text-red-500 text-sm'>
												{' '}
												{errors.unidade.message}{' '}
											</span>
										)}
									</div>
									<div className='flex gap-[30px] flex-col xl:flex-row transition-all duration-300'>
										<div className='flex flex-col w-full text-start'>
											<p className='text-[#004A80] text-[16px] font-medium '>
												{' '}
												Data da Notificação{' '}
											</p>
											<input
												{...register('dataNotificacao')}
												name='dataNotificacao'
												readOnly
												defaultValue={
													new Date()
														.toISOString()
														.split('T')[0]
												}
												type='hidden'
											/>
											<div className='border-[1px] rounded-md text-gray-500 border-[#E3E3E3] py-1 px-2 h-[40px] flex items-center'>
												{new Date().toLocaleDateString(
													'pt-BR',
												)}
											</div>
											{triedToSubmit &&
												errors.dataNotificacao && (
													<span className='text-red-500 text-sm'>
														{' '}
														{
															errors
																.dataNotificacao
																.message
														}{' '}
													</span>
												)}
										</div>

										<div className='flex flex-col w-full text-start'>
											<p className='text-[#004A80] text-[18px ] font-medium '>
												{' '}
												Data da Ocorrência{' '}
											</p>
											<input
												{...register('data_ocorrencia')}
												name='data_ocorrencia'
												type='date'
												max={
													new Date()
														.toISOString()
														.split('T')[0]
												}
												placeholder=''
												className='border-[1px] rounded-md text-gray-500 border-[#E3E3E3] py-1 px-2 placeholder:[#757575] h-[40px]'
											/>
											{triedToSubmit &&
												errors.data_ocorrencia && (
													<span className='text-red-500 text-sm'>
														{' '}
														{
															errors
																.data_ocorrencia
																.message
														}{' '}
													</span>
												)}
										</div>

										<div className='flex flex-col w-full text-start'>
											<p className='text-[#004A80] text-[18px ] font-medium '>
												{' '}
												Setor onde ocorreu o
												incidente{' '}
											</p>
											<SetorComboBox
												setores={setores}
												name='setor'
												value={watch('setor')}
												hasError={
													triedToSubmit &&
													!!errors.setor
												}
												onChange={(val) =>
													setValue('setor', val, {
														shouldValidate: true,
													})
												}
												onBlur={() => {}}
											/>
											{triedToSubmit && errors.setor && (
												<span className='text-red-500 text-sm'>
													{errors.setor.message !==
													'Invalid input: expected string, received undefined'
														? errors.setor.message
														: 'campo obrigatório'}
													{console.log(
														errors.setor.message,
													)}
												</span>
											)}
										</div>

										<div className='flex flex-col w-full text-start'>
											<p className='text-[#004A80] text-[18px ] font-medium '>
												{' '}
												Turno{' '}
											</p>
											<select
												{...register('turno')}
												name='turno'
												className='border-[1px] rounded-md text-gray-500 border-[#E3E3E3] py-1 px-2 placeholder:[#757575] h-[40px]'>
												<option value=''>
													{' '}
													selecione o turno{' '}
												</option>
												{Object.values(choices).length >
													0 &&
													Object.values(
														choices?.turnos,
													)?.map((item, index) => (
														<option
															key={
																item +
																index +
																'turn'
															}
															value={item[0]}>
															{' '}
															{item}{' '}
														</option>
													))}
											</select>
											{triedToSubmit && errors.turno && (
												<span className='text-red-500 text-sm'>
													{' '}
													{errors.turno.message}{' '}
												</span>
											)}
										</div>
									</div>
									<div className='flex flex-col p-[20px] border-[2px] rounded-md border-[#F4F4F4] gap-[15px] w-full'>
										<div className='flex gap-[30px] flex-row flex-wrap'>
											<div className='flex flex-col gap-[8px]'>
												<p className='text-[#004A80] text-[16px] font-medium '>
													{' '}
													O fato foi relacionado a um
													paciente?{' '}
												</p>
												<div className='flex gap-3 flex-wrap'>
													<label
														htmlFor='sim'
														className='flex gap-[8px]'>
														<input
															{...register(
																'envolve_paciente',
															)}
															type='radio'
															name='envolve_paciente'
															className='accent-[#004A80]'
															value={'true'}
															id='sim'
														/>
														<p className='text-[#757575] text-[16px] font-medium '>
															{' '}
															Sim{' '}
														</p>
													</label>

													<label
														htmlFor='nao'
														className='flex gap-[8px]'>
														<input
															{...register(
																'envolve_paciente',
															)}
															type='radio'
															name='envolve_paciente'
															className='accent-[#004A80]'
															value={'false'}
															id='nao'
														/>
														<p className='text-[#757575] text-[16px] font-medium '>
															{' '}
															Não{' '}
														</p>
													</label>
												</div>
											</div>
											{envolve_paciente === 'true' && (
												<div className='flex gap-[30px] flex-col lg:flex-row transition-all duration-300'>
													<div className='h-[65px] hidden xl:block w-[2px] bg-[#E3E3E3]'></div>
													<div className='flex flex-col w-full gap-2'>
														<p className='text-[#004A80] text-[16px] font-medium text-start'>
															{' '}
															Sexo{' '}
														</p>
														<div className='flex gap-[15px]'>
															{Object.values(
																choices,
															).length > 0 &&
																Object.values(
																	choices.sexo_paciente,
																).map(
																	(
																		item,
																		index,
																	) => (
																		<label
																			htmlFor={
																				item
																			}
																			key={
																				index +
																				item +
																				'sexo_paciente'
																			}
																			className='flex gap-[8px]'>
																			<input
																				{...register(
																					'sexo_paciente',
																				)}
																				type='radio'
																				value={
																					Object.keys(
																						choices.sexo_paciente,
																					)[
																						index
																					]
																				}
																				id={
																					item
																				}
																				className='accent-[#004A80]'
																			/>
																			<p className='text-[#757575] text-[16px] font-medium '>
																				{' '}
																				{
																					item
																				}{' '}
																			</p>
																		</label>
																	),
																)}
														</div>

														{triedToSubmit &&
															(errors.sexo_paciente ||
																(watch()
																	.sexo_paciente ===
																	'' &&
																	watch()
																		.envolve_paciente ===
																		true)) && (
																<span className='text-red-500 text-sm text-start'>
																	{' '}
																	selecione
																	uma das
																	opções{' '}
																</span>
															)}
													</div>

													<div className='flex flex-col w-full max-w-[266px]'>
														<p className='text-[#004A80] text-[16px] font-medium text-start'>
															{' '}
															Data de
															Nascimento{' '}
														</p>
														<input
															{...register(
																'nascimento_paciente',
																{
																	setValueAs:
																		(v) =>
																			v ===
																			''
																				? null
																				: v,
																},
															)}
															name='nascimento_paciente'
															type='date'
															placeholder=''
															max={
																new Date()
																	.toISOString()
																	.split(
																		'T',
																	)[0]
															}
															className='border-[1px] rounded-md text-[#757575] border-[#E3E3E3] py-1 px-2 placeholder:[#757575] h-[40px]'
														/>

														{triedToSubmit &&
															errors.nascimento_paciente && (
																<span className='text-red-500 text-sm text-start'>
																	{' '}
																	{resolveMsg(
																		errors
																			.nascimento_paciente
																			.message,
																	)}{' '}
																</span>
															)}
													</div>

													<div className='flex flex-col w-full'>
														<p className='text-[#004A80] text-[16px] font-medium text-start'>
															{' '}
															Prontuário/BE{' '}
														</p>
														<input
															{...register(
																'prontuario',
																{
																	setValueAs:
																		(v) =>
																			v ===
																			''
																				? null
																				: v,
																},
															)}
															name='prontuario'
															maxLength={15}
															type='text'
															placeholder='ex: 0000000000'
															className='border-[1px] rounded-md border-[#E3E3E3] py-1 px-2 w-full max-w-[266px] placeholder:text-[#a7a7a7] h-[40px] text-[16px] font-normal text-[#757575]'
														/>
														{triedToSubmit &&
															errors.prontuario && (
																<span className='text-red-500 text-sm text-start'>
																	{' '}
																	campo
																	obrigatório{' '}
																</span>
															)}
													</div>
												</div>
											)}
										</div>

										{triedToSubmit &&
											errors.envolve_paciente && (
												<span className='text-red-500 text-sm text-start'>
													{' '}
													{errors.envolve_paciente
														.message &&
														'selecione uma das opções'}{' '}
												</span>
											)}

										{envolve_paciente === 'true' && (
											<div className='flex flex-col text-start'>
												<p className='text-[#004A80] text-[18px ] font-medium '>
													{' '}
													Nome do paciente{' '}
												</p>
												<input
													{...register(
														'nome_paciente',
														{
															setValueAs: (v) =>
																v === ''
																	? null
																	: v,
														},
													)}
													name='nome_paciente'
													type='text'
													placeholder='Nome'
													className='border-[1px] rounded-md border-[#E3E3E3] py-1 px-2 placeholder:[#757575] h-[40px]'
												/>
												{triedToSubmit &&
													errors.nome_paciente && (
														<span className='text-red-500 text-sm'>
															{' '}
															campo
															obrigatório{' '}
														</span>
													)}
											</div>
										)}
									</div>

									{/* IDENTIFICAÇÃO DO NOTIFICADOR */}
									<div className='flex gap-[30px] flex-col xl:flex-row transition-all duration-300 bg-[#F4F4F4] p-[20px] rounded-md'>
										<div className='flex flex-col w-full'>
											<div className='flex gap-2'>
												<p className='text-[#004A80] text-[16px] font-medium '>
													{' '}
													Identificação do
													Notificador{' '}
												</p>
												<p className='text-[#999999] text-[16px] font-normal '>
													{' '}
													(opcional){' '}
												</p>
											</div>
											<input
												{...register('notificante')}
												type='text'
												placeholder='Nome'
												className='border-[1px] rounded-md text-gray-500 border-[#E3E3E3] py-1 px-2 placeholder:text-[#E3E3E3] h-[40px]'
											/>
										</div>

										<div className='flex flex-col w-full'>
											<div className='flex gap-2'>
												<p className='text-[#004A80] text-[16px] font-medium '>
													{' '}
													Setor Notificador{' '}
												</p>
												<p className='text-[#999999] text-[16px] font-normal '>
													{' '}
													(obrigatório){' '}
												</p>
											</div>
											<SetorComboBox
												setores={setores}
												name='notificante_setor'
												value={watch(
													'notificante_setor',
												)}
												hasError={
													triedToSubmit &&
													!!errors.notificante_setor
												}
												onChange={(val) =>
													setValue(
														'notificante_setor',
														val,
														{
															shouldValidate: true,
														},
													)
												}
												onBlur={() => {}}
											/>
											{triedToSubmit &&
												errors.notificante_setor && (
													<span className='text-red-500 text-sm text-start'>
														{resolveMsg(
															errors
																.notificante_setor
																.message,
														)}
													</span>
												)}
										</div>

										<div className='flex flex-col w-full'>
											<div className='flex gap-2'>
												<p className='text-[#004A80] text-[16px] font-medium '>
													{' '}
													Cargo Notificador{' '}
												</p>
												<p className='text-[#999999] text-[16px] font-normal '>
													{' '}
													(opcional){' '}
												</p>
											</div>
											<input
												{...register(
													'notificante_cargo',
												)}
												type='text'
												placeholder='Nome'
												className='border-[1px] rounded-md text-gray-500 border-[#E3E3E3] py-1 px-2 placeholder:text-[#E3E3E3] h-[40px]'
											/>
										</div>
									</div>

									{/* INCIDENTES */}
									<div className='flex flex-col p-[20px] border-[2px] rounded-md border-[#F4F4F4] gap-[8px] text-start'>
										<p className='text-[#004A80] text-[18px ] font-medium '>
											{' '}
											Incidentes e não conformidades{' '}
										</p>
										<select
											{...register('tipo_incidente')}
											className='border-[1px] rounded-md text-gray-500 border-[#E3E3E3] py-1 px-2 w-full placeholder:[#757575] h-[40px]'>
											<option value=''>
												{' '}
												Selecione o incidente{' '}
											</option>
											{Object.values(choices).length >
												0 &&
												Object.values(
													choices.tipo_incidente,
												).map((item, index) => (
													<option
														value={
															Object.keys(
																choices.tipo_incidente,
															)[index]
														}
														key={
															index +
															item +
															'tipo_incidente'
														}>
														{' '}
														{item}{' '}
													</option>
												))}
										</select>
										{triedToSubmit &&
											errors.tipo_incidente && (
												<span className='text-red-500 text-sm'>
													{' '}
													{
														errors.tipo_incidente
															.message
													}{' '}
												</span>
											)}

										{tipo_incidente === 'OUTROS' && (
											<div className='flex flex-col gap-[8px] w-[50%]'>
												<input
													{...register(
														'tipo_incidente_outros',
													)}
													type='text'
													placeholder='Descreva o incidente: '
													className=' h-[40px] my-auto p-[12px] border-[#E3E3E3] placeholder:[#757575] rounded-md border-[1px] focus:outline-none focus:ring-0 text-[#929292]'
												/>
												{triedToSubmit &&
													watch()
														.tipo_incidente_outros ===
														'' &&
													watch().tipo_incidente ===
														'OUTROS' && (
														<span className='text-red-500 text-sm'>
															{' '}
															campo
															obrigatório{' '}
														</span>
													)}
											</div>
										)}

										<div className='flex gap-[30px] mt-[15px] transition-all duration-300 '>
											<div className='flex flex-col gap-[16px]'>
												<p className='text-[#004A80] text-[16px] font-medium '>
													{' '}
													O fato foi presenciado por
													alguém?{' '}
												</p>
												<div className='flex gap-[15px]'>
													<label
														htmlFor='simPres'
														className='flex gap-[8px]'>
														<input
															{...register(
																'fato_presenciado',
															)}
															type='radio'
															name='fato_presenciado'
															className='accent-[#004A80]'
															value='true'
															id='simPres'
														/>
														<p className='text-[#757575] text-[16px] font-medium '>
															{' '}
															Sim{' '}
														</p>
													</label>

													<label
														htmlFor='naoPres'
														className='flex gap-[8px]'>
														<input
															{...register(
																'fato_presenciado',
															)}
															type='radio'
															name='fato_presenciado'
															className='accent-[#004A80]'
															value='false'
															id='naoPres'
														/>
														<p className='text-[#757575] text-[16px] font-medium '>
															{' '}
															Não{' '}
														</p>
													</label>
												</div>
											</div>

											{fato_presenciado === 'true' && (
												<div className='h-[70px] my-auto w-[1px] bg-gray-300 '></div>
											)}
											{fato_presenciado === 'true' && (
												<div className='flex flex-col gap-[8px] w-[50%]'>
													<p className='text-[#004A80] text-[16px] font-medium '>
														{' '}
														Nome{' '}
													</p>
													<input
														{...register(
															'quem_presenciou',
															{
																setValueAs: (
																	v,
																) =>
																	v === ''
																		? null
																		: v,
															},
														)}
														type='text'
														placeholder='Nome'
														className=' h-[40px] my-auto p-[12px] border-[#E3E3E3] placeholder:[#757575] rounded-md border-[1px] focus:outline-none focus:ring-0 text-[#929292]'
													/>
													{triedToSubmit &&
														errors.quem_presenciou && (
															<span className='text-red-500 text-sm'>
																{' '}
																{resolveMsg(
																	errors
																		.quem_presenciou
																		.message,
																)}{' '}
															</span>
														)}
												</div>
											)}
										</div>

										{triedToSubmit &&
											errors.fato_presenciado && (
												<span className='text-red-500 text-sm'>
													{' '}
													{errors.fato_presenciado
														.message &&
														'selecione uma das opções'}{' '}
												</span>
											)}
									</div>

									<div className='flex flex-col gap-2'>
										<p className='text-[#004A80] text-[16px] text-start font-medium '>
											{' '}
											Descrição da ocorrência{' '}
										</p>
										<textarea
											{...register('descricao')}
											name='descricao'
											placeholder='Mínimo de 3 caracteres'
											className='border-[1px] text-[#757575] h-[111px] rounded-md border-[#E3E3E3] p-2 placeholder:text-[#d8d8d8] resize-none'
										/>
										{triedToSubmit && errors.descricao && (
											<span className='text-red-500 text-sm text-start'>
												{' '}
												{resolveMsg(
													errors.descricao.message,
												)}{' '}
											</span>
										)}
									</div>

									<div className='flex flex-col gap-2'>
										<p className='text-[#004A80] text-[16px] text-start font-medium '>
											{' '}
											Ações imediatas tomadas{' '}
										</p>
										<textarea
											{...register('acoes_imediatas')}
											name='acoes_imediatas'
											placeholder='Mínimo de 3 caracteres'
											className='border-[1px] text-[#757575] h-[111px] rounded-md border-[#E3E3E3] p-2 placeholder:text-[#d8d8d8] resize-none'
										/>
										{triedToSubmit &&
											errors.acoes_imediatas && (
												<span className='text-red-500 text-sm text-start'>
													{' '}
													{resolveMsg(
														errors.acoes_imediatas
															.message,
													)}{' '}
												</span>
											)}
									</div>

									<div className='flex flex-col gap-2'>
										<p className='text-[#004A80] text-[16px] text-start font-medium '>
											{' '}
											Existe alguma medida em vigor para
											prevenir tal incidente? Qual?{' '}
										</p>
										<textarea
											{...register('medida_preventiva')}
											name='medida_preventiva'
											placeholder='Mínimo de 3 caracteres'
											className='border-[1px] text-[#757575] h-[111px] rounded-md border-[#E3E3E3] p-2 placeholder:text-[#d8d8d8] resize-none'
										/>
										{triedToSubmit &&
											errors.medida_preventiva && (
												<span className='text-red-500 text-sm text-start'>
													{' '}
													{resolveMsg(
														errors.medida_preventiva
															.message,
													)}{' '}
												</span>
											)}
									</div>

									<div className=' sm:w-full flex justify-center sm:justify-end'>
										<button
											type='button'
											onClick={submitHandler}
											className={`p-2 font-normal rounded-md w-[50%] sm:w-[250px] transition-all duration-300 ${submitClass}
                    
                    ${
						Object.keys(errors).length === 0 && !triedToSubmit
							? 'bg-[#004A80] text-white hover:bg-[#1d4663]'
							: 'hover:bg-[#E3E3E3]'
					}
                    `}>
											Enviar
										</button>
									</div>
									<div className='text-red-600 w-full text-[14px] flex justify-end'>
										<p>{createError}</p>
									</div>
								</form>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

// ── Componente interno de combobox de setor ──────────────────────────────────
function SetorComboBox({ setores, value, onChange, onBlur, hasError, name }) {
	const [inputText, setInputText] = useState('');
	const [open, setOpen] = useState(false);
	const [selectedValue, setSelectedValue] = useState('');
	const wrapperRef = useRef(null);

	// Sincroniza texto exibido quando valor externo muda (ex: reset do form)
	useEffect(() => {
		if (!value) {
			setInputText('');
			setSelectedValue('');
		}
	}, [value]);

	// Fecha dropdown ao clicar fora
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
				setOpen(false);
				// Se não há valor selecionado, limpa o texto
				if (!selectedValue) setInputText('');
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () =>
			document.removeEventListener('mousedown', handleClickOutside);
	}, [selectedValue]);

	const filtered = setores.filter((s) =>
		s.nome.toLowerCase().includes(inputText.toLowerCase()),
	);

	const handleInputChange = (e) => {
		const val = e.target.value;
		setInputText(val);
		setOpen(true);
		// Qualquer edição invalida a seleção anterior
		if (selectedValue) {
			setSelectedValue('');
			onChange(''); // notifica o react-hook-form que o valor é inválido
		}
	};

	const handleSelect = (setor) => {
		setInputText(setor.nome);
		setSelectedValue(setor.nome);
		onChange(setor.nome); // notifica o react-hook-form com o valor válido
		setOpen(false);
	};

	return (
		<div
			ref={wrapperRef}
			className='relative w-full'>
			<input
				name={name}
				type='text'
				value={inputText}
				onChange={handleInputChange}
				onFocus={() => setOpen(true)}
				onBlur={onBlur}
				placeholder='pesquise um setor'
				autoComplete='off'
				className={`border-[1px] rounded-md text-gray-500 py-1 px-2 h-[40px] w-full focus:outline-none ${
					hasError && !selectedValue
						? 'border-red-500'
						: 'border-[#E3E3E3]'
				}`}
			/>
			{open && filtered.length > 0 && (
				<ul className='absolute z-50 w-full bg-white border border-[#E3E3E3] rounded-md shadow-md max-h-[180px] overflow-y-auto mt-1'>
					{filtered.map((setor, index) => (
						<li
							key={index + setor.nome}
							onMouseDown={() => handleSelect(setor)} // mouseDown antes do blur
							className=' text-start px-3 py-2 text-[#757575] hover:bg-[#F0F4F8] cursor-pointer text-sm'>
							{setor.nome}
						</li>
					))}
				</ul>
			)}
			{open && filtered.length === 0 && inputText.length > 0 && (
				<div className='absolute z-50 w-full bg-white border border-[#E3E3E3] rounded-md shadow-md mt-1 text-start px-3 py-2 text-sm text-[#a0a0a0]'>
					Nenhum setor encontrado
				</div>
			)}
		</div>
	);
}
// ─────────────────────────────────────────────────────────────────────────────
